import type { ParticipantType } from "~/modules/cbm";

export type EventProperties = Record<string, any>;

export interface AnalyticsInstance {
    track: (eventName: string, additionalValues?: EventProperties) => void;
}

export enum EVENTS {
    AcceptTaskCompleted = "AcceptTask Completed",
    RejectTaskCompleted = "RejectTask Completed",
    SetAttributesCompleted = "SetAttributes Completed",
    SetCurrentActivityCompleted = "SetCurrentActivity Completed",
    WrapupTaskCompleted = "WrapupTask Completed",
    CompleteTaskCompleted = "CompleteTask Completed",
    SetTaskAttributesCompleted = "SetTaskAttributes Completed",
    StartVoiceTaskTransferCompleted = "StartVoiceTaskTransfer Completed",
    CancelVoiceTaskTransfer = "CancelVoiceTaskTransfer Completed",
    KickParticipantCompleted = "KickParticipant Completed",
    EndTaskCompleted = "EndTask Completed",
    HoldParticipantCompleted = "HoldParticipant Completed",
    UnholdParticipantCompleted = "UnholdParticipant Completed",
    SetWorkerActivityCompleted = "SetWorkerActivity Completed",
    SetWorkerAttributesCompleted = "SetWorkerAttributes Completed",
    StartOutboundCallCompleted = "StartOutboundCall Completed",
    StartOutboundEmailTaskCompleted = "StartOutboundEmailTask Completed",
    EndConferenceForAllCompleted = "EndConferenceForAll Completed",
    StartExternalWarmVoiceTaskTransferCompleted = "StartExternalWarmVoiceTaskTransferCompleted",
    UpdateTokenCompleted = "UpdateToken Completed",
    ClientRolesCompleted = "client.roles Completed",
    ClientTokenCompleted = "client.token Completed",
    AddParticipantCompleted = "AddParticipant Completed",
    MonitorCallCompleted = "MonitorCall Completed",
    LeaveConversationChannelCompleted = "LeaveConversationChannel Completed",
    PauseConversationChannelCompleted = "PauseConversationChannel Completed",
    ResumeConversationChannelCompleted = "ResumeConversationChannel Completed",
    GetTaskParticipantsCompleted = "GetTaskParticipants Completed",
    StartChannelTransfer = "StartChannelTransfer Completed",
    GetConversationByTaskCompleted = "GetConversationByTask Completed",
    GetChannelTransfersCompleted = "GetChannelTransfers Completed",
    RemoveEmailParticipantCompleted = "RemoveEmailParticipant Completed",
    GetConversationsUserCompleted = " GetConversationsUser Completed",
    AddEmailParticipantCompleted = "AddEmailParticipant Completed",
    GetPausedConversationsCompleted = "GetPausedConversations Completed",
    GetCallByTaskCompleted = "GetCallByTask Completed"
}

type ErrorPayload = { error?: string; warning?: string };

type Payloads = {
    [EVENTS.AddEmailParticipantCompleted]: { taskSid?: string; participantLevel?: string; participantSid?: string };
    [EVENTS.GetChannelTransfersCompleted]: { taskSid?: string; withNotes?: boolean };
    [EVENTS.GetConversationByTaskCompleted]: { taskSid?: string; conversationSid?: string };
    [EVENTS.GetConversationsUserCompleted]: {
        userSid?: string;
        identity?: never ;
    };
    [EVENTS.GetPausedConversationsCompleted]: { pageSize?: number };
    [EVENTS.LeaveConversationChannelCompleted]: { taskSid?: string; interactionSid?: string; channelSid?: string };
    [EVENTS.PauseConversationChannelCompleted]: { taskSid?: string; interactionSid?: string; channelSid?: string };
    [EVENTS.RemoveEmailParticipantCompleted]: { taskSid?: string; participantSid?: string };
    [EVENTS.ResumeConversationChannelCompleted]: {
        interactionSid?: string;
        channelSid?: string;
        reservationSid?: string;
        conversationSid?: string;
    };
    [EVENTS.StartChannelTransfer]: {
        taskSid?: string;
        to?: string;
        interactionSid?: string;
        channelSid?: string;
        workerParticipantSid?: string;
        notesSid?: string;
        transferType?: "warm" | "cold";
    };
    [EVENTS.StartOutboundEmailTaskCompleted]: {
        to?: never ;
        taskQueueSid?: string;
        workflowSid?: string;
        taskSid?: string;
        interactionSid?: string;
        reservationSid?: string;
        conversationSid?: string;
    };
    [EVENTS.AcceptTaskCompleted]: { taskSid?: string; reservationSid?: string };
    [EVENTS.RejectTaskCompleted]: { taskSid?: string; reservationSid?: string };
    [EVENTS.EndTaskCompleted]: { taskSid?: string; reservationSid?: string };
    [EVENTS.WrapupTaskCompleted]: { taskSid?: string; reservationSid?: string };
    [EVENTS.CompleteTaskCompleted]: { taskSid?: string; reservationSids?: string[] };
    [EVENTS.GetTaskParticipantsCompleted]: { taskSid?: string; reservationSid?: string };
    [EVENTS.StartVoiceTaskTransferCompleted]: {
        taskSid?: string;
        reservationSid?: string;
        toSid?: string;
        mode?: "WARM" | "COLD";
    };
    [EVENTS.CancelVoiceTaskTransfer]: { taskSid?: string; reservationSid?: string; outgoingTransferSid?: string };

    [EVENTS.SetWorkerActivityCompleted]: {
        targetWorkerSid?: string;
        activitySid?: string;
    };
    [EVENTS.SetWorkerAttributesCompleted]: {
        targetWorkerSid?: string;
        mergeExisting?: boolean;
    };
    [EVENTS.GetCallByTaskCompleted]: {
        taskSid?: string;
        customVoiceDevice?: boolean;
        callSid?: string;
        isIncomingCall?: boolean;
    };
    [EVENTS.MonitorCallCompleted]: {
        taskSid?: string;
        reservationSid?: string;
        callSid?: string;
    };
    [EVENTS.StartOutboundCallCompleted]: {
        toNumber?: never ;
        fromNumber?: string;
        taskQueueSid?: string;
        workflowSid?: string;
        taskSid?: string;
        customVoiceDevice?: boolean;
        callSid?: string;
    };

    [EVENTS.AddParticipantCompleted]: {
        taskSid?: string;
        participantSid?: string;
        participantType?: ParticipantType;
        phoneNumber?: never ;
    };
    [EVENTS.EndConferenceForAllCompleted]: {
        taskSid?: string;
        reservationSid?: string;
    };
    [EVENTS.HoldParticipantCompleted]: {
        taskSid?: string;
        reservationSid?: string;
        targetParticipantSid?: string;
        participantSid?: string;
        participantType?: ParticipantType;
    };
    [EVENTS.KickParticipantCompleted]: {
        taskSid?: string;
        reservationSid?: string;
        targetParticipantSid?: string;
        participantSid?: string;
        participantType?: ParticipantType;
    };
    [EVENTS.UnholdParticipantCompleted]: {
        taskSid?: string;
        reservationSid?: string;
        targetParticipantSid?: string;
        participantSid?: string;
        participantType?: ParticipantType;
    };
    [EVENTS.StartExternalWarmVoiceTaskTransferCompleted]: {
        taskSid?: string;
        reservationSid?: string;
        phoneNumber?: never ;
    };

    [EVENTS.SetAttributesCompleted]: {
        mergeExisting?: boolean;
    };
    [EVENTS.SetCurrentActivityCompleted]: {
        activitySid?: string;
        rejectPendingReservations?: boolean;
    };
    [EVENTS.SetTaskAttributesCompleted]: {
        taskSid?: string;
        mergeExisting?: boolean;
    };
};

export type EventPayloads = {
    
    [K in EVENTS]: K extends keyof Payloads ? Payloads[K] & ErrorPayload : ErrorPayload;
};
